/**
 * MessageList — scrollable message list with auto-scroll
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ExpiredQuestionNotice } from './question-expired.js'
import { useTranslation } from '@papan/i18n'
import { DEFAULT_PERSONA_ID } from '../core/persona.js'
import { ThinkingOrb } from 'thinking-orbs'
import { useThemeOptional } from '@papan/core-ui/ui'
import { MessageBubble, CompactionBlock } from './MessageBubble.js'
import { PermissionPrompt } from './PermissionPrompt.js'
import { QuestionPrompt } from './QuestionPrompt.js'
import { A2UIQuestionCard, parseA2UIQuestion } from './a2ui/A2UIQuestionCard.js'
import { AccessApprovalCard } from './AccessApprovalCard.js'
import { PdappIconCard, pdappIconCardProps } from './PdappIconCard.js'
import { parsePdappIconRequest, type Placement } from '@papan/types'
import { parseAccessRequest } from './registerAccessTools.js'
import { CredentialPrompt, type CredentialCardAnswer } from './CredentialPrompt.js'
import type { CredRequest, FieldType } from './useSessionCredentials.js'
import type { MessagePart, PermissionRequest, QuestionRequest } from './hooks.js'
import type { MessageAction } from './MessageActionsMenu.js'
import { isCompactionSummary, isSyntheticUserTurn } from './compaction-predicates.js'
import { hasRunningToolCall, describeTurnActivity, type TurnActivityLike } from './turn-activity.js'

interface MessageItem {
  id: string
  role: string
  parts: MessagePart[]
  modelID?: string
  personaId?: string
  timeCreated?: number
  timeCompleted?: number
  /** USD dollars — the legacy field, unchanged in meaning. */
  cost?: number
  /**
   * The settled BILLING-native amount for this message (parent + subagents),
   * and its currency. Persisted by the worker at save time; absent on older
   * messages, which withholds the badge rather than converting at read time.
   */
  costBillingMicro?: number
  costCurrency?: string
  parentID?: string
  /** System-written user turn (compaction nudge / overflow replay). */
  synthetic?: boolean
  /** Sent mid-run and steered into the running turn (#481 item 4). */
  steer?: boolean
  /** E7 Codex round 8: a steer whose post had no answer — not confirmed yet (a reload settles it). */
  steerUnconfirmed?: boolean
  /** Assistant message that IS a compaction summary. */
  isSummary?: boolean
}

interface Props {
  messages: MessageItem[]
  streamingText: string
  streamingReasoning: string
  pendingPermission: PermissionRequest | null
  onPermissionReply: (permissionId: string, action: 'allow' | 'deny', always?: boolean, feedback?: string) => void
  /** All pending questions — parallel HITL tools stack their cards. */
  pendingQuestions: QuestionRequest[]
  /** I198 / D56: shown where a question card was when its late answer came back
   *  409 QUESTION_EXPIRED (the worker had given up on it). */
  expiredQuestionNotice?: ExpiredQuestionNotice | null
  /** `result` is the mutation-approval outcome, threaded to the worker as
   *  the `_result` side-channel (AccessApprovalCard). */
  onQuestionReply: (questionId: string, answers: string[][], entryId?: string, envMapping?: Record<string, string>, result?: unknown, credentialSaved?: boolean, credential?: CredentialCardAnswer) => void | Promise<void>
  onQuestionDismiss: (questionId: string) => void
  /** The element the workspace has selected, as a readable path — `null` when
   *  nothing is. Only the PDApp-icon card reads it, to offer a placement. */
  selectedElement?: string | null
  /** That element's tag, lowercased. Void elements cannot take a child, so the
   *  two "inside" placements are withheld for them. */
  selectedElementTag?: string | null
  /** The workspace's root folder. In a PDApp workspace it IS the app being
   *  edited, which the icon card leaves out of its copy list — an app linking to
   *  itself is never what the author meant. */
  workspaceFolderId?: string | null
  /** Active tool calls — used to extract credential request fields */
  /**
   * Live tool calls by call id. LOAD-BEARING, not cosmetic: this is how a
   * question's EMITTING tool is resolved, which is what distinguishes a real
   * access card from one a prompt-injected turn forged with the ungated
   * `question` tool (see parseAccessRequest). Every surface rendering a full
   * agent session must pass it — omitting it silently takes the fail-open
   * branch for every card on that surface, which is how the conflict chat sat
   * unguarded.
   */
  activeToolCalls?: Map<string, { toolName: string; input: Record<string, unknown> }>
  /** Callback when credentials are resolved — stores in session.
   *  Values are NOT passed up; the per-turn resolver looks them up
   *  server-side from the keybook entry. */
  onCredentialResolved?: (envMapping: Record<string, string>, entryId?: string, callId?: string, fieldMap?: Record<string, string>, opts?: { oneTimeOnly?: boolean; fieldTypes?: Record<string, FieldType> }) => boolean | void | Promise<boolean | void>
  /** Active session credentials — used to auto-resolve without showing
   *  form, AND as the single source of truth for the cred form's
   *  provider/file/label/envMapping when restoring after refresh. */
  sessionCredentials?: CredRequest[]
  isProcessing: boolean
  /** Work the turn is doing outside any tool call — today the post-`bash` tree
   *  sync. null when there is nothing to report. */
  turnActivity?: TurnActivityLike | null
  /** A manual "Summarize session" is running.
   *
   *  Rendered HERE rather than only on the desktop context ring: `HeroChat` —
   *  which is the whole mobile chat surface — has no ring, so mobile had no
   *  feedback at all. Every surface renders this list, so one indicator covers
   *  both, and it sits where the user is already looking rather than in the
   *  composer chrome. */
  summarizing?: boolean
  /** Current retry state (null = not retrying) */
  retry?: { attempt: number; error: string; delay: number } | null
  /** #762: the connection to a running turn was lost; the chat is checking quietly until it is back. */
  reconnecting?: boolean
  /** Display options */
  showReasoning?: boolean
  showToolDetails?: boolean
  showModelInfo?: boolean
  /**
   * Whether the per-message cost badge renders at all (0174).
   *
   * Separate from `showModelInfo` — that is the USER's display preference and
   * this is the deployment's policy, so folding them together would let a
   * customer's toggle re-enable a figure a platform admin hid. Defaults to
   * FALSE: a caller that has not resolved the policy yet must withhold rather
   * than flash a number the deployment may have chosen to hide.
   */
  showCost?: boolean

  showTimestamps?: boolean
  wrapLines?: boolean
  /** Message action handler (for 3-dot menu on user messages) */
  onMessageAction?: (action: MessageAction, messageId: string) => void
  /** Workspace ID for subagent message fetching */
  workspaceId?: string
  /** ID of the message currently being edited (null = none) */
  editingMessageId?: string | null
  /** Start editing a user message */
  onEditStart?: (messageId: string) => void
  /** Cancel editing */
  onEditCancel?: () => void
  /** Send the edited message */
  onEditSend?: (messageId: string, editedText: string) => void
  /** Open a file by VFS path (for attachment chip clicks) */
  onOpenFile?: (path: string, at?: { line?: number }) => void
  /** A `file` attachment part was moved to the drive (#662) — threaded to
   *  each `MessageBubble` → `AttachmentChip`. */
  onMoved?: (partId: string, drive: { fileId: string; path: string }) => void
  /** Current persona ID — badges the streaming bubble before the
   *  assistant message lands. */
  activePersona?: string
  /** Host-provided welcome shown while the session has no messages
   *  (HeroChat's starter cards, ChatPanel's ProjectWelcome). Omitted →
   *  the empty session renders blank. */
  emptyState?: React.ReactNode
  /** Session ID — used for scroll position save/restore across tab switches */
  sessionId?: string | null
  /** Render controls (model, attachments, send) inside the edit bubble */
  renderEditControls?: (editText: string, onCancel: () => void, onSend: () => void) => React.ReactNode
}

// ─── Scroll position cache ────────────────────────────────────────────
// Stores scrollTop per session. Scoped in-memory (cleared on page reload).
// Limited to 50 entries to cap RAM usage.
const scrollCache = new Map<string, number>()
const SCROLL_CACHE_LIMIT = 50

function saveScrollPosition(sessionId: string, scrollTop: number) {
  scrollCache.set(sessionId, scrollTop)
  // Evict oldest entries if over limit
  if (scrollCache.size > SCROLL_CACHE_LIMIT) {
    const first = scrollCache.keys().next().value
    if (first) scrollCache.delete(first)
  }
}

function getScrollPosition(sessionId: string): number | undefined {
  return scrollCache.get(sessionId)
}

/** HTML elements that cannot contain anything — the two "inside" placements are
 *  meaningless for them. The spec's void set, not a guess. */
const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
])

/** A remembered choice must not outlive the selection that made it legal: pick
 *  `after` when `inside-*` is no longer on offer, or the card would submit a
 *  placement its own dropdown is not showing. */
function allowedPlace(chosen: Placement | undefined, tag: string | null): Placement {
  const v = chosen ?? 'after'
  if ((v === 'inside-start' || v === 'inside-end') && VOID_ELEMENTS.has((tag ?? '').toLowerCase())) return 'after'
  return v
}

export function MessageList({
  messages,
  streamingText,
  streamingReasoning,
  pendingPermission,
  onPermissionReply,
  pendingQuestions,
  expiredQuestionNotice = null,
  summarizing,
  onQuestionReply,
  onQuestionDismiss,
  selectedElement = null,
  selectedElementTag = null,
  workspaceFolderId = null,
  activeToolCalls: activeToolCalls,
  onCredentialResolved,
  sessionCredentials,
  isProcessing,
  turnActivity,
  retry,
  reconnecting,
  showReasoning,
  showToolDetails,
  showModelInfo,
  showCost = false,
  showTimestamps,
  wrapLines,
  onMessageAction,
  workspaceId,
  editingMessageId,
  onEditStart,
  onEditCancel,
  onEditSend,
  onOpenFile,
  onMoved,
  activePersona,
  emptyState,
  sessionId,
  renderEditControls,
}: Props) {
  // `collab` for the icon-placement choices; `agent` for the one string this
  // component renders itself — the expired-question note (I198 / D56).
  const { t: tPlace } = useTranslation('collab')
  const { t: tAgent } = useTranslation('agent')
  const bottomRef = useRef<HTMLDivElement>(null)
  /**
   * The PDApp-icon cards' placement choices, keyed BY QUESTION.
   *
   * One shared value was wrong twice over: two concurrent asks (two subagents)
   * moved each other's dropdown and answered with the same placement, and a
   * second ask inherited the first's choice while its own preview reset — a
   * decision shown about a different element.
   *
   * It lives here rather than in the card because these cards are reconciled by
   * `key` inside one `.map`, so they are NOT remounted per render — but they ARE
   * unmounted when the question leaves `pendingQuestions`, and a map keyed by id
   * keeps each card's choice its own either way.
   */
  const [iconPlace, setIconPlace] = useState<Record<string, Placement>>({})
  /**
   * The placement control the card hands to whichever modal it opens, or
   * `undefined` — which is what makes the control disappear when there is
   * nothing to be relative to.
   *
   * Element-relative, deliberately. A page-relative pair was tried so that the
   * control would always be present, and taken back out: the web is made of
   * containers, so "where" is answered by pointing at one, and two families of
   * placement meaning different things is a bigger idea than the choice is
   * worth. With no selection the icon goes to the end of the page.
   */
  const iconPlacementFor = useCallback((qid: string) => (selectedElement
    ? {
        label: tPlace('iconPlacement.label'),
        options: [
          { value: 'before' as const, label: tPlace('iconPlacement.before') },
          { value: 'after' as const, label: tPlace('iconPlacement.after') },
          // Withheld for a VOID element. The editor gates these on parse5's
          // `innerEnd` — the set with an explicit end tag — and the chat, which
          // has no loc, asks the tag instead. Offering them anyway sent the
          // model "make the icon the FIRST CHILD of this <img>", which it would
          // have followed into uncomposable HTML.
          ...(VOID_ELEMENTS.has((selectedElementTag ?? '').toLowerCase())
            ? []
            : [
                { value: 'inside-start' as const, label: tPlace('iconPlacement.insideStart') },
                { value: 'inside-end' as const, label: tPlace('iconPlacement.insideEnd') },
              ]),
        ],
        value: allowedPlace(iconPlace[qid], selectedElementTag),
        onChange: (v: Placement) => setIconPlace((m) => ({ ...m, [qid]: v })),
      }
    // DEPS: `selectedElementTag` and `tPlace` are both read above. They were
    // missing (the list said `t`), so the two "inside" options kept whatever
    // legality they had when the selection last changed identity.
    : undefined), [selectedElement, selectedElementTag, iconPlace, tPlace])

  // Papan's three themes collapse to the orb's two grounds. `syncAll` is the
  // same expression ThemeToggle uses — with it on, every area follows
  // `workspace`, so reading `theme.chat` directly would render the orb for a
  // theme the panel is not using.
  // OPTIONAL on purpose: this tints one decorative canvas, so a missing
  // provider must not take the transcript down with it. `dim` is the shipped
  // default and is a dark ground, so 'dark' is the right fallback.
  const themeCtx = useThemeOptional()
  const chatTheme = themeCtx
    ? (themeCtx.settings.theme.syncAll ? themeCtx.settings.theme.workspace : themeCtx.settings.theme.chat)
    : 'dim'
  const orbTheme: 'dark' | 'light' = chatTheme === 'light' ? 'light' : 'dark'
  const containerRef = useRef<HTMLDivElement>(null)
  const stuckRef = useRef(true)
  const prevMsgLenRef = useRef(messages.length)
  const prevSessionIdRef = useRef(sessionId)
  const restoringScrollRef = useRef(true)  // true on mount — scroll to bottom or saved position

  // Track last message identity so scroll re-triggers when messages are
  // reloaded from the API after completion (temp IDs → real DB IDs).
  const lastMsg = messages[messages.length - 1]
  const lastMsgKey = lastMsg ? `${lastMsg.id}:${lastMsg.parts.length}` : ''

  // Save scroll position when switching away, restore when switching in
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      // Save outgoing session's scroll position
      if (prevSessionIdRef.current && containerRef.current) {
        saveScrollPosition(prevSessionIdRef.current, containerRef.current.scrollTop)
      }
      restoringScrollRef.current = true
      prevSessionIdRef.current = sessionId
    }
  }, [sessionId])

  // Restore scroll position after messages render for the new session
  // useLayoutEffect runs synchronously after DOM update, before paint — so we
  // set scrollTop before the browser paints, preventing a flash at wrong position
  useLayoutEffect(() => {
    if (!restoringScrollRef.current || !sessionId) return
    if (messages.length === 0) return // messages haven't loaded yet

    const el = containerRef.current
    if (!el) return

    const saved = getScrollPosition(sessionId)
    if (saved !== undefined) {
      el.scrollTop = saved
      stuckRef.current = el.scrollHeight - saved - el.clientHeight < 80
    } else {
      // No saved position — scroll to bottom
      el.scrollTop = el.scrollHeight
      stuckRef.current = true
    }
    restoringScrollRef.current = false
  }, [sessionId, messages.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sticky scroll — track whether user is near the bottom
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onScroll = () => {
      if (restoringScrollRef.current) return
      const { scrollTop, scrollHeight, clientHeight } = el
      stuckRef.current = scrollHeight - scrollTop - clientHeight < 80
      if (sessionId) saveScrollPosition(sessionId, scrollTop)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [sessionId])

  // Auto-scroll: useLayoutEffect runs before paint.
  // Measure actual gap instead of relying on stuckRef — the scroll listener
  // may not have processed yet after a previous programmatic scroll.
  useLayoutEffect(() => {
    const newMsgAdded = messages.length > prevMsgLenRef.current
    prevMsgLenRef.current = messages.length
    if (restoringScrollRef.current) return

    const el = containerRef.current
    if (!el) return

    // Always scroll to bottom when a new user message is sent
    const lastMsg = messages[messages.length - 1]
    if (newMsgAdded && lastMsg?.role === 'user') {
      console.log('[scroll] NEW USER MSG → force bottom')
      el.scrollTop = el.scrollHeight
      stuckRef.current = true
      return
    }

    const gap = el.scrollHeight - el.scrollTop - el.clientHeight
    const isAtBottom = gap < 80

    // If we were stuck (at bottom), stay stuck — content grew below us.
    // Only the scroll event listener can set stuckRef=false (user scrolled up).
    // This useLayoutEffect only re-engages (false→true) when gap shrinks to <80.
    if (stuckRef.current || isAtBottom) {
      el.scrollTop = el.scrollHeight
      stuckRef.current = true
    } else {
      const maxScroll = el.scrollHeight - el.clientHeight
      if (el.scrollTop > maxScroll) {
        el.scrollTop = maxScroll
      }
    }
    // `summarizing` is in the deps because the compaction indicator is a real
    // element that appears mid-turn and ADDS HEIGHT below the last message.
    // Without it this effect never runs for that insertion, so a reader sitting
    // at the bottom gets the indicator pushed half-off the fold — reported as
    // "the loading state appears a bit overflow".
    //
    // It does NOT force a scroll: it re-runs the same stuck-or-at-bottom rule
    // as every other trigger, so someone who has scrolled up to read history is
    // left where they are rather than being yanked to the end by a background
    // housekeeping step they did not ask for.
  }, [messages.length, lastMsgKey, streamingText, pendingPermission, pendingQuestions, expiredQuestionNotice, retry, reconnecting, summarizing])

  if (messages.length === 0 && !streamingText) {
    return emptyState ? <>{emptyState}</> : null
  }

  // Precompute per-message "has changes from this point onward" (reverse scan)
  const changesFromIndex: boolean[] = new Array(messages.length).fill(false)
  let seenChanges = false
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.parts?.some((p: any) => p.type === 'patch' && p.snapshots?.length > 0)) seenChanges = true
    changesFromIndex[i] = seenChanges
  }

  return (
    <div ref={containerRef} data-testid="chat-transcript" className="flex-1 min-w-0 overflow-y-auto py-3 pl-5 pr-3 space-y-4">
      {messages.map((msg, i) => {
        // Compaction boundary → render as a standalone CompactionBlock.
        //
        // The summary text lives on the ASSISTANT message parented to this
        // boundary, not on the compaction part: process() writes the part with
        // only { auto, overflow, tail_start_id }, so `compactionPart.summary`
        // is undefined for every row written since the OpenCode rewrite. Read
        // it off the pair, and keep the part field as the legacy fallback —
        // otherwise the block expands to the generic "earlier messages were
        // summarized" line and the summary we paid to generate is never shown
        // to anyone (#438).
        const compactionPart = msg.parts.find(p => p.type === 'compaction')
        if (compactionPart && msg.role === 'user') {
          const paired = messages.find(m => isCompactionSummary(m) && m.parentID === msg.id)
          const pairedText = paired?.parts
            .filter((p): p is Extract<MessagePart, { type: 'text' }> => p.type === 'text')
            .map(p => p.content)
            .join('\n\n')
            .trim()
          // How much this boundary actually covered: the messages between the
          // PREVIOUS boundary and this one. Counted here rather than carried on
          // the row because no column records it, and because this is the only
          // place that already holds the ordered transcript.
          //
          // Excludes the boundaries themselves and the summary rows they pair
          // with — those are compaction's own bookkeeping, not conversation.
          const here = messages.findIndex(m => m.id === msg.id)
          let prevBoundary = -1
          for (let k = here - 1; k >= 0; k--) {
            if (messages[k]!.parts.some(p => p.type === 'compaction')) { prevBoundary = k; break }
          }
          // Counts what the reader can SEE. Excludes the boundaries, the summary
          // rows they pair with, AND the synthetic "continue" turn an AUTO
          // compaction writes — the renderer below hides all three, so counting
          // one would print a number that disagrees with the messages on
          // screen, off by exactly one on the case this label exists to
          // explain: a fresh /compact after an auto-compaction.
          const covered = messages
            .slice(prevBoundary + 1, here)
            .filter(m => !m.parts.some(p => p.type === 'compaction')
              && !isCompactionSummary(m)
              && !(m.role === 'user' && isSyntheticUserTurn(m)))
            .length
          return (
            <CompactionBlock
              key={msg.id}
              summary={pairedText || (compactionPart as any).summary}
              at={msg.timeCreated}
              covered={covered}
            />
          )
        }
        // Its summary is rendered by the block above, so drop the raw message.
        if (isCompactionSummary(msg)) {
          return null
        }
        // A user turn compaction wrote, not one the person typed. Persisted so
        // the MODEL sees a well-formed transcript; showing it to the user puts
        // words in their mouth. `synthetic` on every text part is the legacy
        // shape (the nudge carried it per-part before the message-level flag).
        if (msg.role === 'user' && isSyntheticUserTurn(msg)) {
          return null
        }

        const isLastAssistant = msg.role === 'assistant' && i === messages.length - 1
        // Compute response duration: assistant.timeCompleted - parent user.timeCreated
        let duration: number | undefined
        if (msg.role === 'assistant' && msg.timeCompleted && msg.parentID) {
          const parent = messages.find(m => m.id === msg.parentID)
          if (parent?.timeCreated) {
            duration = msg.timeCompleted - parent.timeCreated
          }
        }
        return (
          <div key={msg.id} id={`agent-msg-${msg.id}`} className="scroll-mt-4">
          <MessageBubble
            role={msg.role}
            parts={msg.parts}
            streamingText={isLastAssistant ? streamingText : undefined}
            streamingReasoning={isLastAssistant ? streamingReasoning : undefined}
            modelID={msg.modelID}
            personaId={msg.personaId}
            duration={duration}
            // Straight off the message. The worker persists the settled
            // billing-native rollup — parent PLUS subagents, summed from
            // per-row stamps — at save time, because that attribution exists
            // nowhere else: `agent_usage` has no `parent_message_id`, and a
            // child's own row is keyed to a message this list never renders.
            //
            // No conversion, no lookup, no rate. Absent means withheld.
            settledCost={showCost && msg.costBillingMicro != null && msg.costCurrency
              ? { amount: msg.costBillingMicro / 1_000_000, currency: msg.costCurrency }
              : undefined}
            timeCreated={msg.timeCreated}
            steer={msg.steer}
            {...(msg.steerUnconfirmed ? { steerUnconfirmed: { label: tAgent('steer.notConfirmed'), hint: tAgent('steer.notConfirmedHint') } } : {})}
            isComplete={!!msg.timeCompleted}
            showReasoning={showReasoning}
            showToolDetails={showToolDetails}
            showModelInfo={showModelInfo}
            showTimestamps={showTimestamps}
            wrapLines={wrapLines}
            messageId={msg.id}
            onMessageAction={onMessageAction}
            hasChanges={changesFromIndex[i]}
            workspaceId={workspaceId}
            sessionId={sessionId}
            isEditing={editingMessageId === msg.id}
            onEditStart={onEditStart}
            onEditCancel={onEditCancel}
            onEditSend={onEditSend}
            onOpenFile={onOpenFile}
            onMoved={onMoved}
            renderEditControls={editingMessageId === msg.id ? renderEditControls : undefined}
          />
          </div>
        )
      })}

      {/* Streaming text when no messages yet have the assistant response */}
      {streamingText && messages[messages.length - 1]?.role !== 'assistant' && (
        <MessageBubble
          role="assistant"
          parts={[]}
          streamingText={streamingText}
          streamingReasoning={streamingReasoning}
          // Was omitted, and MessageBubble defaults it to `true` — so with the
          // new default OFF a user still saw reasoning in this transient
          // bubble. The settled render above already passes it.
          showReasoning={showReasoning}
          personaId={activePersona || DEFAULT_PERSONA_ID}
        />
      )}

      {/* Compaction in progress — see the `summarizing` prop. */}
      {summarizing && (
        <div
          className="my-3 flex items-center gap-2"
          role="status"
          aria-live="polite"
          data-testid="compaction-running"
        >
          <div className="flex-1 border-t themed-border" />
          {/* `chat-shimmer` rather than a flat muted colour, matching the
            *  Thinking indicator — both say "still working", so both sweep.
            *  It replaces `themed-text-muted` instead of joining it: the class
            *  sets `-webkit-text-fill-color: transparent` and paints the glyphs
            *  from its own gradient, whose ends are already `--text-muted`, so
            *  a colour alongside it is dead weight.
            *
            *  Safe around the canvas: `background-clip: text` clips this span's
            *  OWN background, not its children, and `-webkit-text-fill-color`
            *  touches glyphs only — the orb renders untouched, as it already
            *  does inside the identically-classed Thinking row. Reduced motion
            *  is handled by the existing media query on the class. */}
          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-wider px-2 py-0.5 chat-shimmer">
            {/* `theme` is passed EXPLICITLY, never left on 'auto'. The library
             *  detects a `dark`/`light` class or `prefers-color-scheme`; Papan
             *  uses `.theme-dark` / `.theme-dim` / `.theme-light`, so 'auto'
             *  would miss all three and fall through to the OS preference —
             *  which renders dark ink on a dark panel for anyone running the
             *  app in Dim with a light OS. `dim` is a dark ground, so it maps
             *  to 'dark' (the library's names describe the BACKGROUND, and it
             *  draws light ink for a dark one).
             *
             *  aria-hidden because the orb ships its own role="img"
             *  aria-label="Thinking…", and this row is already a live region
             *  labelled by the text beside it — without this a screen reader
             *  announces "Thinking… Summarizing conversation". */}
            <ThinkingOrb
              state="breathing"
              size={20}
              theme={orbTheme}
              aria-hidden
              style={{ marginBlock: -5 }}
            />
            Summarizing conversation
          </span>
          <div className="flex-1 border-t themed-border" />
        </div>
      )}

      {/* Permission prompt */}
      {pendingPermission && (
        <PermissionPrompt
          request={pendingPermission}
          onReply={(action, always, feedback) => onPermissionReply(pendingPermission.id, action, always, feedback)}
        />
      )}

      {/* Question prompts — one card PER pending question (parallel HITL
          tools stack). Credential requests get a specialized form; header
          'A2UI' + parseable surface renders the A2UI card (an unparseable
          payload falls through to the generic prompt). */}
      {pendingQuestions.map(pq => {
        if (pq.questions[0]?.header === 'Credentials') {
          return (
            <CredentialPromptWithFields
              key={pq.id}
              request={pq}
              onReply={(answers, _credentialEnv, entryId, envMapping, credentialSaved, credential) => onQuestionReply(pq.id, answers, entryId, envMapping, undefined, credentialSaved, credential)}
              onDismiss={() => onQuestionDismiss(pq.id)}
              onCredentialResolved={onCredentialResolved}
              sessionCredentials={sessionCredentials}
            />
          )
        }
        // Pass the tool that RAISED this question. `header` is model-writable
        // (see parseAccessRequest), so the emitting call is the only thing here
        // the model cannot choose.
        const accessReq = parseAccessRequest(pq, activeToolCalls?.get(pq.tool?.callID ?? '')?.toolName)
        if (accessReq) {
          return (
            <AccessApprovalCard
              key={pq.id}
              request={pq}
              meta={accessReq}
              onReply={(answers, result) => Promise.resolve(onQuestionReply(pq.id, answers, undefined, undefined, result))}
            />
          )
        }
        // #788 — "choose a PDApp icon". Recognised by its header AND by the
        // tool that raised it, like the access card: a header is model-writable
        // and the emitting call is not.
        const iconReq = pq.questions[0]
          ? parsePdappIconRequest(
            pq.questions[0],
            // Server-stamped where it exists (it rides the question, so it
            // survives a reload); the live tool-call map otherwise.
            pq.tool?.toolName ?? activeToolCalls?.get(pq.tool?.callID ?? '')?.toolName,
          )
          : null
        if (iconReq) {
          return (
            <PdappIconCard
              key={pq.id}
              request={pdappIconCardProps(pq.id, iconReq)}
              onReply={async (answers, result) => { await onQuestionReply(pq.id, answers, undefined, undefined, result) }}
              placement={iconPlacementFor(pq.id)}
              excludeFolderId={workspaceFolderId}
            />
          )
        }
        const a2uiSurface = parseA2UIQuestion(pq)
        if (a2uiSurface) {
          return (
            <A2UIQuestionCard
              key={pq.id}
              request={pq}
              surface={a2uiSurface}
              onReply={(answers) => onQuestionReply(pq.id, answers)}
              onDismiss={() => onQuestionDismiss(pq.id)}
              onOpenFile={onOpenFile}
            />
          )
        }
        return (
          <QuestionPrompt
            key={pq.id}
            request={pq}
            onReply={(answers) => onQuestionReply(pq.id, answers)}
            onDismiss={() => onQuestionDismiss(pq.id)}
          />
        )
      })}

      {/* I198 / D56: a late answer to a question the worker gave up on. */}
      {expiredQuestionNotice && (
        <div role="status" data-testid="question-expired-note" className="text-xs themed-text-muted rounded border themed-border px-3 py-2">
          {tAgent(expiredQuestionNotice.noteKey)}
        </div>
      )}

      {/* Retry indicator */}
      {retry && (
        <div className="flex gap-1.5 items-center">
          <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse shrink-0" />
          <span className="text-xs themed-text-muted">
            Retrying (attempt {retry.attempt})... {Math.round(retry.delay / 1000)}s
          </span>
        </div>
      )}

      {/* #762: the live connection was lost and the chat is waiting for it to come back — the retry indicator's look
       *  (a waiting state, not an error); the turn may well be running on the server. */}
      {reconnecting && isProcessing && (
        <div role="status" data-testid="chat-reconnecting" className="flex gap-1.5 items-center">
          <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse shrink-0" />
          <span className="text-xs themed-text-muted">{tAgent('status.reconnecting')}</span>
        </div>
      )}

      {/* Processing indicator — hide only while a tool card is showing its OWN
       *  running state. `hasRunningToolCall` asks that; the predicate it
       *  replaced (`parts.length > 0`) asked whether the turn had ever called
       *  a tool, which stays true for the rest of the turn and hid the
       *  indicator through every gap between tools — including the whole
       *  post-`bash` tree sync, which is minutes on a fresh clone.
       *
       *  `turnActivity` OVERRIDES that suppression rather than being subject to
       *  it: it is sent precisely for work no tool card represents, so if it is
       *  set there is by definition nothing else on screen saying so. Erring
       *  toward one extra indicator is the right direction — the failure being
       *  fixed here is a screen that says nothing at all. */}
      {isProcessing && !streamingText && !pendingPermission && pendingQuestions.length === 0 && !retry && !reconnecting
        && (turnActivity != null || !hasRunningToolCall(messages)) && (
        <span className="inline-flex items-center gap-2 text-xs chat-shimmer" data-testid="thinking-indicator">
          {/* Same orb, same explicit theme, same reason it is aria-hidden: the
           *  word beside it is the label, and the orb ships its own
           *  role="img" aria-label="Thinking…" which would double-announce.
           *  `breathing` is the library's OWN mapping for this state — its
           *  LABELS table reads `breathing: 'Thinking…'` — so it matches here
           *  for the same reason it suits compaction, not merely for looks. */}
          <ThinkingOrb state="breathing" size={20} theme={orbTheme} aria-hidden style={{ marginBlock: -5 }} />
          {describeTurnActivity(turnActivity ?? null) ?? 'Thinking...'}
        </span>
      )}

      <div ref={bottomRef} />
    </div>
  )
}

/** Wrapper that extracts credential fields and wires values through the reply. The source of truth is the
 *  cred_to_env call itself — credInput, JSON-encoded in the question's option description (it survives a page
 *  refresh with the question); the session rows only answer "is this file's credential already bound?". */
function CredentialPromptWithFields({ request, onReply, onDismiss, onCredentialResolved, sessionCredentials }: {
  request: QuestionRequest
  /** Reply through the question pipe. credentialEnv was previously
   *  trusted from the browser; it's now always undefined (server
   *  resolves at the API layer using entryId). envMapping (the SHAPE
   *  of fieldName → envVarName) is passed through so the server can
   *  resolve without waiting on the session_credential row write. */
  onReply: (answers: string[][], credentialEnv?: Record<string, string>, entryId?: string, envMapping?: Record<string, string>, credentialSaved?: boolean, credential?: CredentialCardAnswer) => void
  onDismiss: () => void
  /** Binds the entry to this card's credential row (by its tool call id); resolves true once that row is
   *  written (I198 Codex round — the reply waits for it). */
  onCredentialResolved?: (envMapping: Record<string, string>, entryId?: string, callId?: string, fieldMap?: Record<string, string>, opts?: { oneTimeOnly?: boolean; fieldTypes?: Record<string, FieldType> }) => boolean | void | Promise<boolean | void>
  sessionCredentials?: CredRequest[]
}) {
  // Decode the cred_to_env tool's JSON-stuffed metadata.
  let credInput: {
    file?: string
    label?: string
    fields?: Array<{ name: string; type: string; env?: string }>
    envMapping?: Record<string, string>
    mode?: 'auto' | 'update' | 'new_env'
    provider?: string
    context?: { repo?: string }
  } | undefined
  try {
    const desc = request.questions[0]?.options[0]?.description
    if (desc && desc.startsWith('{')) credInput = JSON.parse(desc)
  } catch { /* not JSON — use defaults */ }

  // Everything the card shows and submits comes from the CURRENT call (round 11): its mapping is what the tool told
  // the model on this run, and the row it binds is found by that call's id — the one way to find it
  const file = credInput?.file
  const label = credInput?.label
  const envMapping = credInput?.envMapping ?? {}
  const provider = credInput?.provider
  const fields = credInput?.fields
  const mode = credInput?.mode || 'auto'

  // Check if this file's credentials are already active in this session
  // update/new_env modes always show the form. With the server-resolved
  // model the cred row no longer carries `values`; an active row with
  // an entryId is enough to know the resolver will produce env vars.
  const activeInSession = mode === 'auto' && sessionCredentials?.find(c =>
    c.status === 'active' && c.file === file && !!c.entryId
  )

  return (
    <CredentialPrompt
      request={request}
      fields={fields}
      file={file}
      label={label}
      envMapping={envMapping}
      force={mode !== 'auto'}
      alreadyInSession={!!activeInSession}
      provider={provider}
      contextRepo={credInput?.context?.repo}
      onReply={(answers) => {
        // Don't send credentialEnv for "Do this later"
        onReply(answers)
      }}
      onDismiss={onDismiss}
      onCredentialResolved={async (entryId, saved, credential) => {
        // Store in session + send through question reply. credentialEnv
        // is always undefined (values are resolved server-side from
        // entryId), but envMapping IS sent so the server can resolve
        // immediately without waiting on the session_credential row
        // — the bind() POST that creates that row races this reply
        // POST, and a stale lookup would land empty env vars in the
        // worker.
        // I198 Codex round: AWAIT the binding before replying (useSessionCredentials:
        // a follow-up must not race the session_credential row) — an expiry's "send a
        // message to continue" relies on it. A failed binding still replies, on purpose:
        // THIS turn does not need the binding — the worker resolves the key from the
        // reply's entryId + envMapping through the keybook — while holding the card for a
        // retry would re-run the save and create a second keybook entry. The cost is a
        // LATER turn, whose per-turn resolver reads the binding: it will ask for the key
        // again. So it is logged, and never claimed as "saved".
        let bound: boolean | void = false
        try {
          // #1441: the card's exact field map rides the binding (its one-time values never do — #1448). The binding names
          // the call's FULL mapping (#1448 D4): the SERVER knows which fields are one-time (the call's own card) and never
          // fills them from the key — a key-only mapping here made the hook's re-sync of the call post the full one back,
          // which the route reads as a change of binding and clears the field map. Codes alone (no key): the row goes quietly.
          bound = await onCredentialResolved?.(envMapping, entryId, request.tool?.callID, credential?.fieldMap, {
            ...(!entryId && credential?.oneTime ? { oneTimeOnly: true } : {}),
            // a subagent's card has no pending row in this tab: the bind's new row still knows its one-time fields (D2)
            ...(fields ? { fieldTypes: Object.fromEntries(fields.map((f) => [f.name, f.type as FieldType])) } : {}),
          })
        } catch (err) {
          console.warn('[cred-prompt] binding the key to this chat failed:', err)
        }
        if (bound !== true) console.warn('[cred-prompt] the key is not bound to this chat — a later turn will ask for it again')
        // `saved` (I198 / D56): THIS submit wrote the keybook entry, the write completed
        // AND the binding landed — only then may an expiry note say "your key was saved".
        onReply([['Submitted']], undefined, entryId, envMapping, saved === true && bound === true, credential)
      }}
      onFolderUpgrade={async (entryId) => {
        // Upgrade the keybook entry from file-level to folder-level
        try {
          const filePath = file
          if (!filePath) return
          const { filesApi } = await import('@papan/files/client')
          const relativePath = filePath.replace(/^\/papan\/?/, '')
          // Get parent folder path
          const parentPath = relativePath.split('/').slice(0, -1).join('/')
          if (!parentPath) return
          const parentResult = await filesApi.resolvePath(parentPath).catch(() => null)
          if (!parentResult?.id) return
          const { keybookApi } = await import('@papan/keybook/client')
          // Was: PATCH metadata { fileId } when binding lived on entry.
          // Now binding is junction-only — add the parent folder via
          // bindFile so the credential covers every file underneath.
          await keybookApi.bindFile(entryId, parentResult.id)
        } catch (err) {
          console.warn('[cred-prompt] Failed to upgrade to folder:', err)
        }
      }}
    />
  )
}
